import {
  Button,
  FileButton,
  Group,
  Image,
  Loader,
  Modal,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { Archive, Images, ImagePlus, Upload } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";

import { useListBrands, useListProducts } from "../../api/generated/endpoints/teams/teams";
import type { Asset } from "../../api/generated/model";
import { MediaMime } from "../../api/generated/zod";
import { LoadAhead, morePages } from "../../ui/LoadAhead";
import { ErrorAlert } from "../ErrorAlert";
import classes from "./ImagePicker.module.css";
import { UploadState, useImageUpload } from "./image-upload";
import { useLibraryPages } from "./library-pages";

/** Live's picker page (`ASSETS_DEFAULT_LIMIT`). */
const PICKER_PAGE = 24;

/** A chosen image: the asset id a request sends, a picture to show meanwhile, and its name. */
export interface PickedImage {
  assetId: string;
  url: string | null;
  /** The full picture, for a caller that draws it rather than a chip (the layer editor). */
  original?: string | null;
  /** The file's name, or the product's: what a chip beside the picture says. */
  name: string;
  /** Its size where the library knows it (a video's, for Reframe's ratio rule). */
  width?: number | null;
  height?: number | null;
  /** A video's or an audio's length, where the library knows it. */
  durationMs?: number | null;
  /** The file's size in bytes, where the library knows it (a source's limit, the dub's). */
  bytes?: number | null;
}

/** The brand whose library and products a picker shows, and where an upload goes. */
export interface PickerLibrary {
  teamId: string;
  brandId: string;
}

/** The picker's tabs: the brand's products' photos, and its library (with upload). */
export type PickerTab = "products" | "library";

export const IMAGE_TYPES = MediaMime.options.filter((mime) => mime.startsWith("image/"));
const TAB_LABEL: Record<PickerTab, string> = { products: "Products", library: "Library" };
const TAB_ICON: Record<PickerTab, ReactNode> = {
  products: <Archive size={16} />,
  library: <Images size={16} />,
};

/** The picture to show for an image asset: its thumbnail, else the original. */
export function pictureOf(asset: Asset): string | null {
  return asset.urls.thumb ?? asset.urls.original;
}

interface ImagePickerProps {
  library: PickerLibrary;
  /** Names the dialog: "Choose image 1", "Choose a product photo". */
  title: string;
  opened: boolean;
  onClose: () => void;
  onPick: (picked: PickedImage) => void;
  /** The tabs, in order; the first opens. One tab shows without a tab row. */
  tabs?: readonly PickerTab[];
  /** An upload in the dialog started or ended: a form waits for it, and asks before it is left
   *  (spec A3d). */
  onBusy?: (busy: boolean) => void;
}

/**
 * Choose an image (A5 decision 27, G1 decision 20): a product's photo, or one of the brand's
 * library images, newest first, or a new upload through the library's one upload path, which
 * the dialog then chooses. The caller sends the asset's id; any ready image of the team may be
 * a logo, a product image or a generation's source.
 */
export function ImagePicker(props: ImagePickerProps) {
  const { library, title, opened, onClose, onPick, tabs = ["library"], onBusy } = props;
  // The upload in flight: closing the dialog cancels it, and the flow deletes its pending asset.
  const running = useRef<AbortController | null>(null);
  const close = () => {
    running.current?.abort();
    onClose();
  };
  const choose = (picked: PickedImage) => {
    onPick(picked);
    onClose();
  };
  const panel = (tab: PickerTab) =>
    tab === "products" ? (
      <ProductPhotos library={library} opened={opened} onChoose={choose} />
    ) : (
      <LibraryImages
        library={library}
        opened={opened}
        onChoose={choose}
        onUploading={(controller) => {
          running.current = controller;
          onBusy?.(controller !== null);
        }}
      />
    );
  return (
    <Modal opened={opened} onClose={close} title={title} size="xl" centered>
      {tabs.length === 1 ? (
        panel(tabs[0]!)
      ) : (
        <Tabs defaultValue={tabs[0]!} keepMounted={false}>
          <Tabs.List mb="md">
            {tabs.map((tab) => (
              <Tabs.Tab key={tab} value={tab} leftSection={TAB_ICON[tab]}>
                {TAB_LABEL[tab]}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          {tabs.map((tab) => (
            <Tabs.Panel key={tab} value={tab}>
              {panel(tab)}
            </Tabs.Panel>
          ))}
        </Tabs>
      )}
    </Modal>
  );
}

interface PanelProps {
  library: PickerLibrary;
  /** The reads run only while the dialog is open. */
  opened: boolean;
  onChoose: (picked: PickedImage) => void;
}

/** One image to choose: its picture (or a box once its signed URL stops loading) and a name. */
interface ChoiceProps {
  label: string;
  caption: string;
  url: string | null;
  onClick: () => void;
}

function Choice(props: ChoiceProps) {
  const [failed, setFailed] = useState(false);
  return (
    <UnstyledButton className={classes.choice} aria-label={props.label} onClick={props.onClick}>
      {props.url === null || failed ? (
        <div className={classes.picture} />
      ) : (
        <Image
          src={props.url}
          alt=""
          fit="cover"
          className={classes.picture}
          onError={() => setFailed(true)}
        />
      )}
      <Text size="xs" truncate className={classes.name}>
        {props.caption}
      </Text>
    </UnstyledButton>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <Stack align="center" gap={6} py="xl" c="var(--cai-color-text-muted)">
      <ImagePlus size={32} strokeWidth={1.5} aria-hidden />
      <Text size="sm">{children}</Text>
    </Stack>
  );
}

/** The brand's products, each with its photos; a photo without a URL is left out. */
function ProductPhotos({ library, opened, onChoose }: PanelProps) {
  const products = useListProducts(library.teamId, library.brandId, undefined, {
    query: { enabled: opened },
  });
  if (products.isPending) return <Loader size="sm" aria-label="Loading products" />;
  if (products.isError) return <ErrorAlert error={products.error} />;
  const withPhotos = products.data
    .map((product) => ({ product, photos: product.images.filter((image) => image.url !== null) }))
    .filter((each) => each.photos.length > 0);
  if (withPhotos.length === 0) {
    return <Empty>No product has a photo yet. Add one on the product's page.</Empty>;
  }
  return (
    <Stack gap="md">
      {withPhotos.map(({ product, photos }) => (
        <Stack key={product.id} gap={6}>
          <Text size="sm" fw={600}>
            {product.name}
          </Text>
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
            {photos.map((photo, at) => (
              <Choice
                key={photo.asset_id}
                label={`Choose ${product.name}, photo ${at + 1}`}
                caption={photo.is_main === true ? "Main photo" : `Photo ${at + 1}`}
                url={photo.url}
                onClick={() =>
                  onChoose({ assetId: photo.asset_id, url: photo.url, name: product.name })
                }
              />
            ))}
          </SimpleGrid>
        </Stack>
      ))}
    </Stack>
  );
}

/** The brand's library images, newest first, and "Upload", which chooses the new image. */
function LibraryImages(
  props: PanelProps & { onUploading: (controller: AbortController | null) => void },
) {
  const { library, opened, onChoose, onUploading } = props;
  const { teamId, brandId } = library;
  // Live's picker pages (`ASSETS_DEFAULT_LIMIT`, 24), the next as the end shows: it cut off at its
  // first 50 (issue 794).
  const images = useLibraryPages(teamId, brandId, { kind: "image", trash: false }, opened, { size: PICKER_PAGE });
  const brandName = useListBrands(teamId, {
    query: { select: (brands) => brands.find((brand) => brand.id === brandId)?.name },
  }).data;
  const upload = useImageUpload({
    teamId,
    brandId,
    onUploaded: (asset) =>
      onChoose({ assetId: asset.id, url: pictureOf(asset), name: asset.name }),
    onUploading,
  });

  const items = images.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="nowrap">
        <Text size="sm" c="var(--cai-color-text-muted)">
          {brandName === undefined ? "The brand's library" : `${brandName}'s library`}
        </Text>
        <FileButton
          accept={IMAGE_TYPES.join(",")}
          onChange={(file) => void upload.send(file)}
          inputProps={{ "aria-label": "Choose an image to upload" }}
        >
          {(buttonProps) => (
            <Button
              {...buttonProps}
              variant="outline"
              leftSection={<Upload size={16} />}
              loading={upload.pending}
            >
              Upload
            </Button>
          )}
        </FileButton>
      </Group>
      <UploadState upload={upload} />
      {images.isPending ? <Loader size="sm" aria-label="Loading images" /> : null}
      {images.isError ? <ErrorAlert error={images.error} /> : null}
      {images.isSuccess && items.length === 0 ? (
        <Empty>No images in this library yet. Upload one.</Empty>
      ) : null}
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
        {items.map((asset) => (
          <Choice
            key={asset.id}
            label={`Choose ${asset.name}`}
            caption={asset.name}
            url={pictureOf(asset)}
            onClick={() => onChoose({ assetId: asset.id, url: pictureOf(asset), name: asset.name })}
          />
        ))}
      </SimpleGrid>
      <LoadAhead more={morePages(images)} loaded={items.length} label="Loading more images" />
    </Stack>
  );
}
